import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Activity,
  ArrowDown,
  ArrowUp,
  Building2,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Users,
} from 'lucide-react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const defaultSummary = {
  total_accounts: 0,
  active_7d: 0,
  active_30d: 0,
  active_7d_prev: 0,
  active_7d_delta: 0,
  repeat_users: null,
  multi_property_users: 0,
  compliance_users: 0,
  high_intent_users: 0,
};

const metricDefinitions = [
  {
    key: 'total_accounts',
    label: 'Total Accounts',
    description: 'All landlord accounts tracked for engagement.',
    icon: Users,
    iconClassName: 'bg-blue-100 text-blue-700',
  },
  {
    key: 'active_7d',
    label: 'Active (7d)',
    description: 'Accounts active in the last 7 days.',
    icon: Activity,
    iconClassName: 'bg-emerald-100 text-emerald-700',
  },
  {
    key: 'active_30d',
    label: 'Active (30d)',
    description: 'Accounts active in the last 30 days.',
    icon: Activity,
    iconClassName: 'bg-royal-100 text-royal-700',
  },
  {
    key: 'high_intent_users',
    label: 'High Intent Users',
    description: 'Accounts showing stronger monetisation readiness.',
    icon: Sparkles,
    iconClassName: 'bg-amber-100 text-amber-700',
  },
  {
    key: 'multi_property_users',
    label: 'Multi-Property Users',
    description: 'Accounts managing two or more properties.',
    icon: Building2,
    iconClassName: 'bg-violet-100 text-violet-700',
  },
  {
    key: 'compliance_users',
    label: 'Requirement-Engaged Users',
    description: 'Accounts active in compliance-oriented workflows.',
    icon: ShieldCheck,
    iconClassName: 'bg-cyan-100 text-cyan-700',
  },
];

const breakdownDefinitions = [
  {
    key: 'repeat_users',
    label: 'Repeat Users',
    description: 'Accounts with repeat recent logins.',
  },
  {
    key: 'multi_property_users',
    label: 'Multi-Property Users',
    description: 'Accounts managing multiple properties.',
  },
  {
    key: 'compliance_users',
    label: 'Requirement-Engaged Users',
    description: 'Accounts engaging with compliance and requirement flows.',
  },
  {
    key: 'high_intent_users',
    label: 'High-Intent Users',
    description: 'Accounts with two or more engagement signals.',
  },
];

const normalizeSummary = (payload = {}) => ({
  ...defaultSummary,
  ...(payload || {}),
});

function MetricCard({ label, description, value, icon: Icon, iconClassName }) {
  return (
    <Card className="border-border/60 shadow-sm">
      <CardContent className="flex items-start justify-between gap-4 p-5">
        <div className="space-y-1">
          <p className="text-sm font-medium text-muted-foreground">{label}</p>
          <p className="text-3xl font-semibold tracking-tight text-navy-900">{value}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
        <div className={`rounded-xl p-3 ${iconClassName}`}>
          <Icon className="h-5 w-5" />
        </div>
      </CardContent>
    </Card>
  );
}

function LoadingState() {
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <Card key={index} className="border-border/60 shadow-sm">
            <CardContent className="space-y-3 p-5">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-8 w-20" />
              <Skeleton className="h-3 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <Card className="border-border/60 shadow-sm">
          <CardHeader>
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-56" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </CardContent>
        </Card>
        <Card className="border-border/60 shadow-sm">
          <CardHeader>
            <Skeleton className="h-5 w-44" />
            <Skeleton className="h-4 w-48" />
          </CardHeader>
          <CardContent className="space-y-3">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} className="h-16 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export const AdminEngagementDashboard = ({ embedded = false }) => {
  const [summary, setSummary] = useState(defaultSummary);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Admin Engagement - SIYAK';
  }, []);

  const fetchSummary = useCallback(async ({ silent = false } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError('');

    try {
      const response = await axios.get(`${API_URL}/api/admin/engagement-summary`, {
        withCredentials: true,
      });
      setSummary(normalizeSummary(response.data));
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to load engagement analytics right now.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const metricCards = useMemo(
    () => metricDefinitions.map((item) => ({ ...item, value: summary?.[item.key] ?? 0 })),
    [summary],
  );

  const breakdownItems = useMemo(
    () => breakdownDefinitions.filter((item) => typeof summary?.[item.key] === 'number'),
    [summary],
  );

  const delta = Number(summary?.active_7d_delta || 0);
  const deltaPositive = delta > 0;
  const deltaNegative = delta < 0;
  const DeltaIcon = deltaNegative ? ArrowDown : ArrowUp;
  const deltaClassName = deltaPositive
    ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
    : deltaNegative
      ? 'text-red-700 bg-red-50 border-red-200'
      : 'text-muted-foreground bg-muted/40 border-border';
  const deltaLabel = `${delta > 0 ? '+' : ''}${delta}`;

  const content = (
    <div className={embedded ? '' : 'min-h-screen bg-navy-50 py-8'}>
      <div className={embedded ? 'space-y-8' : 'mx-auto max-w-7xl px-4 md:px-8'}>
        {!embedded ? (
          <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Badge variant="outline" className="font-medium">Admin</Badge>
                <span>Internal analytics overview</span>
              </div>
              <h1 className="font-heading text-3xl font-bold text-navy-900">Engagement Dashboard</h1>
              <p className="max-w-3xl text-sm text-muted-foreground">
                Monitor account activity, identify higher-intent users, and gauge monetisation readiness using existing admin analytics.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="outline" asChild>
                <Link to="/admin">Back to Admin Dashboard</Link>
              </Button>
              <Button variant="outline" onClick={() => fetchSummary({ silent: true })} disabled={refreshing || loading}>
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        ) : null}

        {loading ? <LoadingState /> : null}

        {!loading && error ? (
          <Card className="border-red-200 bg-red-50 shadow-sm">
            <CardHeader>
              <CardTitle className="text-red-900">Unable to load engagement data</CardTitle>
              <CardDescription className="text-red-800/80">
                {error}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" onClick={() => fetchSummary()}>
                Try again
              </Button>
            </CardContent>
          </Card>
        ) : null}

        {!loading && !error ? (
          <div className="space-y-8">
            <section className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-navy-900">Engagement snapshot</h2>
                <p className="text-sm text-muted-foreground">Current account totals and monetisation-readiness signals.</p>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {metricCards.map((item) => (
                  <MetricCard key={item.key} {...item} />
                ))}
              </div>
            </section>

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.1fr_0.9fr]">
              <section>
                <Card className="border-border/60 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-navy-900">Trend insight</CardTitle>
                    <CardDescription>
                      Active users over the latest 7-day window compared with the previous period.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="rounded-xl border border-border/60 bg-white p-5">
                      <div className="grid gap-4 sm:grid-cols-3">
                        <div>
                          <p className="text-sm text-muted-foreground">Active Users (7d)</p>
                          <p className="mt-2 text-3xl font-semibold text-navy-900">{summary.active_7d}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Previous</p>
                          <p className="mt-2 text-3xl font-semibold text-navy-900">{summary.active_7d_prev}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Change</p>
                          <div className="mt-2 flex items-center gap-2">
                            <span className="text-3xl font-semibold text-navy-900">{deltaLabel}</span>
                            <span className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm font-medium ${deltaClassName}`}>
                              {delta === 0 ? '→' : deltaPositive ? '↑' : '↓'}
                              {delta !== 0 ? <DeltaIcon className="h-3.5 w-3.5" /> : null}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </section>

              <section>
                <Card className="border-border/60 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-navy-900">Engagement breakdown</CardTitle>
                    <CardDescription>
                      High-intent users = accounts with ≥2 engagement signals.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {breakdownItems.map((item) => (
                      <div
                        key={item.key}
                        className="flex items-center justify-between rounded-xl border border-border/60 bg-white px-4 py-3"
                      >
                        <div>
                          <p className="font-medium text-navy-900">{item.label}</p>
                          <p className="text-xs text-muted-foreground">{item.description}</p>
                        </div>
                        <Badge variant="secondary" className="min-w-10 justify-center px-3 py-1 text-sm">
                          {summary[item.key]}
                        </Badge>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </section>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );

  if (embedded) {
    return content;
  }

  return <Layout>{content}</Layout>;
};